import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import { Seo } from '../components/Seo';
import { 
  Shield,
  Database,
  Lock,
  Eye,
  UserX,
  Server,
  CreditCard,
  Mail
} from 'lucide-react';

const Privacy = () => {

  const dataWeCollect = [
    {
      icon: <UserX className="w-5 h-5" />,
      title: 'Account Information',
      description: 'Your name, email address, and a securely hashed version of your password. We never store your actual password.'
    },
    {
      icon: <Database className="w-5 h-5" />,
      title: 'Business Data',
      description: 'Client information, invoices, products, and branding settings you create within the app.'
    },
    {
      icon: <CreditCard className="w-5 h-5" />,
      title: 'Payment Information',
      description: 'We do NOT store your credit card details. All payment processing is handled directly by Stripe, a PCI-DSS Level 1 certified processor.'
    }
  ];

  const thirdPartyServices = [
    {
      name: 'Stripe',
      purpose: 'Payment processing for subscriptions and invoice payments',
      link: 'https://stripe.com/privacy'
    },
    {
      name: 'Mailgun',
      purpose: 'Sending transactional emails (invoices, reminders, campaigns)',
      link: 'https://www.mailgun.com/legal/privacy-policy/'
    }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Seo
        title="Privacy Policy — CreateCollect"
        description="How CreateCollect collects, uses, and protects your data, including email tracking, third-party services, and account deletion."
        path="/privacy"
      />
      {/* Header */}
      <PublicHeader secondaryLabel="About" secondaryHref="/about" />

      {/* Main Content */}
      <main className="flex-1 pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-16">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-heading font-bold mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Our Commitment */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">Our Commitment to You</h2>
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
              <p className="text-lg leading-relaxed">
                CreateCollect is built for freelancers and creatives who deserve straightforward, honest privacy practices. 
                <strong> We do not sell, share, or trade your personal data with third parties for marketing purposes. Ever.</strong>
              </p>
              <p className="text-muted-foreground mt-4">
                CreateCollect is intended for business use by adults 18 and older.
              </p>
            </div>
          </section>

          {/* What We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-6">What Data We Collect</h2>
            <div className="space-y-4">
              {dataWeCollect.map((item, index) => (
                <div key={index} className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center text-primary flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How We Use Your Data */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">How We Use Your Data</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span><strong>To provide our service:</strong> Creating invoices, managing clients, processing payments, and sending emails on your behalf.</span>
              </li>
              <li className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span><strong>To communicate with you:</strong> Sending transactional emails like invoice confirmations and payment receipts.</span>
              </li>
              <li className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span><strong>To improve our service:</strong> Understanding how features are used to make CreateCollect better.</span>
              </li>
            </ul>
          </section>

          {/* Email Tracking */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">Email Tracking</h2>
            <div className="p-4 bg-muted/30 rounded-lg">
              <Mail className="w-6 h-6 text-primary mb-2" />
              <p className="text-muted-foreground leading-relaxed">
                CreateCollect tracks email opens and link clicks for invoice emails and marketing campaigns you send through our platform. This data is used solely to show you delivery and engagement metrics in your dashboard. We do not share this information with third parties for advertising or profiling purposes.
              </p>
            </div>
          </section>

          {/* What We DON'T Do */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">What We Do NOT Do</h2>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span><strong>We do NOT sell your data</strong> to advertisers, data brokers, or any third parties.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span><strong>We do NOT share your client lists</strong> or business information with anyone.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span><strong>We do NOT use your data for targeted advertising</strong> or create marketing profiles.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span><strong>We do NOT store credit card numbers</strong> — Stripe handles all payment card data.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Third Party Services */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">Third-Party Services</h2>
            <p className="text-muted-foreground mb-4">
              We use the following trusted services to operate CreateCollect. Each has their own privacy policy:
            </p>
            <div className="space-y-3">
              {thirdPartyServices.map((service, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold">{service.name}</h3>
                    <p className="text-sm text-muted-foreground">{service.purpose}</p>
                  </div>
                  {service.link && (
                    <a 
                      href={service.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      Privacy Policy →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">Data Security</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <Server className="w-6 h-6 text-primary mb-2" />
                <h3 className="font-semibold mb-1">Secure Storage</h3>
                <p className="text-sm text-muted-foreground">Your data is transmitted over encrypted connections (HTTPS) and stored with restricted access. Payment card details are handled entirely by Stripe and never touch our servers.</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <Lock className="w-6 h-6 text-primary mb-2" />
                <h3 className="font-semibold mb-1">Secure Transmission</h3>
                <p className="text-sm text-muted-foreground">All data is transmitted over HTTPS with TLS encryption.</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <CreditCard className="w-6 h-6 text-primary mb-2" />
                <h3 className="font-semibold mb-1">PCI Compliance</h3>
                <p className="text-sm text-muted-foreground">Payment processing through Stripe meets PCI-DSS Level 1 standards.</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <Shield className="w-6 h-6 text-primary mb-2" />
                <h3 className="font-semibold mb-1">Password Hashing</h3>
                <p className="text-sm text-muted-foreground">Passwords are hashed using industry-standard bcrypt algorithm.</p>
              </div>
            </div>
          </section>

          {/* Data Retention and Deletion */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">Data Retention and Deletion</h2>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-muted-foreground leading-relaxed">
                We retain your account and business data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law (e.g., financial records for tax purposes). Anonymized, aggregated data may be retained for analytics.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">Your Rights</h2>
            <p className="text-muted-foreground mb-4">You have the right to:</p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong>Access</strong> your personal data stored in our system</li>
              <li>• <strong>Update</strong> your account information at any time</li>
              <li>• <strong>Delete</strong> your account and all associated data</li>
              <li>• <strong>Export</strong> your invoices and client data</li>
              <li>• <strong>Unsubscribe</strong> from marketing communications</li>
            </ul>
            <p className="mt-4 text-muted-foreground">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@createcollect.com" className="text-primary hover:underline">
                support@createcollect.com
              </a>
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">Questions?</h2>
            <p className="text-muted-foreground">
              If you have any questions about this Privacy Policy or how we handle your data, 
              please reach out to us at{' '}
              <a href="mailto:support@createcollect.com" className="text-primary hover:underline">
                support@createcollect.com
              </a>{' '}
              or visit our{' '}
              <Link to="/support" className="text-primary hover:underline">
                Support page
              </Link>.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
